import React, { useEffect, useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Button as MuiButton,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  Chip,
  makeStyles,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import { useIntl } from 'react-intl'
import { useRoleServices } from 'data/services'
import { RoleModel, RoleModuleAccessModel } from 'data/models'
import { SecurityModules } from 'enums/SecurityModules'
import Button from 'components/Buttons'
import { createNotification } from 'factories/notificationFactory'

// ─── Display order + labels for modules actually present in the web app ──────
// Mirrors the left-sidebar navigation in components/navigation/index.tsx.
// Desktop-only modules are intentionally omitted — they have no UI here.
// labelId points at translation files (en/zh/ar/fr/de) so the matrix
// follows the user's Display Language.
const MODULE_LABELS: Array<{ id: number; labelId: string; labelDefault: string }> = [
  { id: SecurityModules.Dashboard,         labelId: 'menu.main.dashboard',         labelDefault: 'Dashboard' },
  { id: SecurityModules.Reports,           labelId: 'menu.main.reports',           labelDefault: 'Reports' },
  { id: SecurityModules.Calendar,          labelId: 'menu.main.calendar',          labelDefault: 'Calendar' },
  { id: SecurityModules.JourneyManagement, labelId: 'menu.main.journey',           labelDefault: 'Journey Management' },
  { id: SecurityModules.ScheduledItems,    labelId: 'menu.main.Schedule',          labelDefault: 'Schedule' },
  { id: SecurityModules.DocumentLibrary,   labelId: 'menu.main.library',           labelDefault: 'Library' },
  { id: SecurityModules.TrainingMatrix,    labelId: 'menu.main.trainingMatrix',    labelDefault: 'Training Matrix' },
  // CompletedForms is hidden from the UI — it is auto-synced with BlankForms on save
  { id: SecurityModules.BlankForms,        labelId: 'menu.main.forms',             labelDefault: 'Forms' },
  { id: SecurityModules.Assignments,       labelId: 'menu.main.assignments.header',labelDefault: 'Assignments' },
  { id: SecurityModules.CMMSAssets,        labelId: 'menu.main.assets.header',     labelDefault: 'Assets' },
  { id: SecurityModules.People,            labelId: 'settings.menu.users',         labelDefault: 'Users' },
  { id: SecurityModules.OrganizationChart, labelId: 'settings.menu.teams',           labelDefault: 'Teams' },
  { id: SecurityModules.LocationTracking,  labelId: 'menu.main.location.tracking',    labelDefault: 'Map Management' },
  { id: SecurityModules.DirectMessages,    labelId: 'menu.main.communication',         labelDefault: 'Communication' },
  { id: SecurityModules.Notifications,     labelId: 'settings.menu.notifications',       labelDefault: 'Notifications' },
  { id: SecurityModules.ActivityLog,       labelId: 'settings.menu.activityLog',         labelDefault: 'Activity Log' },
  { id: SecurityModules.CompanyProfile,    labelId: 'settings.menu.companyProfile',      labelDefault: 'Company Profile' },
  { id: SecurityModules.UserProfile,       labelId: 'settings.menu.profile',             labelDefault: 'User Profile' },
  { id: SecurityModules.SecuritySettings,  labelId: 'settings.menu.security',            labelDefault: 'Security' },
  { id: SecurityModules.SystemPreferences, labelId: 'settings.menu.systemPreferences',   labelDefault: 'System Preferences' },
  { id: SecurityModules.RolesPermissions,  labelId: 'settings.menu.rolesPermissions',    labelDefault: 'Roles & Permissions' },
  { id: SecurityModules.Session,           labelId: 'settings.menu.session',             labelDefault: 'Session' },
]

// Must match backend Workaware.Library.Enums.ModuleNoAcessVisibilityTypes:
//   Invisible = 1, GreyedOut = 2
const VISIBILITY_HIDDEN = 1
const VISIBILITY_GREYED_OUT = 2

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === 'dark'
  const activeBg = isDark ? 'rgba(76, 144, 227, 0.18)' : '#eff6ff'
  const activeFg = isDark ? '#7cb7ff' : '#1e40af'
  const hoverBg = isDark ? 'rgba(255,255,255,0.06)' : '#f0f4f8'
  return ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    minHeight: 0,
    background: theme.palette.background.paper,
  },
  leftPane: {
    width: 300,
    borderRight: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flexShrink: 0,
  },
  leftHeader: {
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  newRoleBtn: {
    backgroundColor: '#163A64 !important',
    color: '#fff !important',
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 4,
    padding: '5px 12px',
    boxShadow: 'none',
    fontSize: 13,
    '&:hover': { backgroundColor: '#0f2a4a !important', boxShadow: 'none' },
  },
  leftTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.primary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.4px',
  },
  rolesList: {
    flex: 1,
    overflowY: 'auto' as const,
    minHeight: 0,
  },
  roleRow: {
    padding: '10px 16px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    '&:hover': { background: hoverBg },
  },
  roleRowActive: {
    background: activeBg,
    '& $roleName': { color: activeFg },
  },
  roleName: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  roleMeta: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  rightPane: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  rightScrollArea: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '24px',
    minHeight: 0,
  },
  rightHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  fieldRow: {
    marginBottom: 14,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.text.primary,
    marginBottom: 6,
    display: 'block',
  },
  matrix: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: 'hidden',
    marginTop: 8,
  },
  matrixHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr 100px 180px',
    padding: '8px 12px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: isDark ? '#0b1322' : '#f8f9fa',
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  matrixBody: {
    overflowY: 'auto' as const,
  },
  matrixRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 100px 180px',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: 13,
    color: theme.palette.text.primary,
    '&:last-child': { borderBottom: 'none' },
  },
  saveFooter: {
    flexShrink: 0,
    background: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: '14px 24px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  defaultBadge: {
    height: 18,
    fontSize: 10,
  },
  loading: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
})})

const SecurityPage: React.FC = () => {
  const classes = useStyles()
  const intl = useIntl()
  const roleServices = useRoleServices()

  const [roles, setRoles] = useState<RoleModel[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState<number | null>(null)

  // Editable form state for the currently selected role
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isAdminLike, setIsAdminLike] = useState(false)
  const [isManagerLike, setIsManagerLike] = useState(false)
  const [moduleAccess, setModuleAccess] = useState<RoleModuleAccessModel[]>([])
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)

  const [createOpen, setCreateOpen] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createDescription, setCreateDescription] = useState('')
  const [createIsAdminLike, setCreateIsAdminLike] = useState(false)
  const [createIsManagerLike, setCreateIsManagerLike] = useState(false)

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [reassignTo, setReassignTo] = useState<number | ''>('')
  const [deleting, setDeleting] = useState(false)

  const selectedRole = useMemo(() => roles.find((r) => r.Id === selectedId) || null, [roles, selectedId])
  const otherAdminRolesExist = useMemo(
    () => roles.some((r) => r.IsAdminLike && r.Id !== selectedId),
    [roles, selectedId]
  )

  // ── Load roles on mount ─────────────────────────────────────────────────────
  const loadRoles = async (selectAfterId?: number) => {
    setLoading(true)
    try {
      const list = await roleServices.getRoles()
      setRoles(list || [])
      if (selectAfterId !== undefined) {
        setSelectedId(selectAfterId)
      } else if (list && list.length > 0 && selectedId === null) {
        setSelectedId(list[0].Id)
      }
    } catch {
      createNotification.error(
        intl.formatMessage({ id: 'roles.loadError', defaultMessage: 'Failed to load roles' })
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRoles()
    // eslint-disable-next-line
  }, [])

  // ── Load module access whenever selected role changes ───────────────────────
  useEffect(() => {
    if (!selectedRole) {
      setName('')
      setDescription('')
      setIsAdminLike(false)
      setIsManagerLike(false)
      setModuleAccess([])
      setDirty(false)
      return
    }

    setName(selectedRole.RoleName)
    setDescription(selectedRole.Description || '')
    setIsAdminLike(selectedRole.IsAdminLike)
    setIsManagerLike(selectedRole.IsManagerLike)
    setDirty(false)

    let cancelled = false
    ;(async () => {
      try {
        const rows = await roleServices.getModuleAccess(selectedRole.Id)
        if (cancelled) return
        // Backfill missing modules with defaults so UI shows all 29.
        const byModule = new Map<number, RoleModuleAccessModel>()
        rows.forEach((r) => byModule.set(r.ModuleId, r))
        const full: RoleModuleAccessModel[] = MODULE_LABELS.map((m) => {
          const existing = byModule.get(m.id)
          if (existing) return existing
          const blank = new RoleModuleAccessModel()
          blank.RoleId = selectedRole.Id
          blank.ModuleId = m.id
          blank.Allowed = false
          blank.ModuleNoAccessVisibilityType = VISIBILITY_HIDDEN
          return blank
        })
        setModuleAccess(full)
      } catch {
        if (!cancelled) {
          createNotification.error(
            intl.formatMessage({ id: 'roles.accessLoadError', defaultMessage: 'Failed to load role permissions' })
          )
        }
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line
  }, [selectedRole?.Id])

  // ── Field change handlers ──────────────────────────────────────────────────
  const handleAllowedChange = (moduleId: number, allowed: boolean) => {
    setModuleAccess((prev) =>
      prev.map((r) => (r.ModuleId === moduleId ? { ...r, Allowed: allowed } as RoleModuleAccessModel : r))
    )
    setDirty(true)
  }

  const handleVisibilityChange = (moduleId: number, vis: number) => {
    setModuleAccess((prev) =>
      prev.map((r) => (r.ModuleId === moduleId ? { ...r, ModuleNoAccessVisibilityType: vis } as RoleModuleAccessModel : r))
    )
    setDirty(true)
  }

  // ── Save current role ──────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedRole) return
    if (!name.trim()) {
      createNotification.error(
        intl.formatMessage({ id: 'roles.nameRequired', defaultMessage: 'Role name is required' })
      )
      return
    }
    // Block demoting the last admin role from the client side too.
    if (selectedRole.IsAdminLike && !isAdminLike && !otherAdminRolesExist) {
      createNotification.error(
        intl.formatMessage({
          id: 'roles.lastAdmin',
          defaultMessage: 'Cannot remove admin status from the last admin role.',
        })
      )
      return
    }

    setSaving(true)
    try {
      await roleServices.updateRole(selectedRole.Id, {
        RoleName: name.trim(),
        Description: description,
        IsAdminLike: isAdminLike,
        IsManagerLike: isManagerLike,
      })
      // Auto-sync CompletedForms to match Forms (BlankForms) — CompletedForms is
      // hidden from the UI so the admin only needs to toggle "Forms" once.
      const blankFormsEntry = moduleAccess.find(m => m.ModuleId === SecurityModules.BlankForms)
      let itemsToSave = [...moduleAccess]
      if (blankFormsEntry) {
        const completedIdx = itemsToSave.findIndex(m => m.ModuleId === SecurityModules.CompletedForms)
        const syncedEntry = { ...blankFormsEntry, ModuleId: SecurityModules.CompletedForms }
        if (completedIdx >= 0) {
          itemsToSave[completedIdx] = syncedEntry
        } else {
          itemsToSave = [...itemsToSave, syncedEntry]
        }
      }
      await roleServices.updateModuleAccess(selectedRole.Id, itemsToSave)
      createNotification.success(
        intl.formatMessage({ id: 'roles.saved', defaultMessage: 'Role saved' })
      )
      await loadRoles(selectedRole.Id)
      setDirty(false)
    } catch (error: any) {
      const message = error?.response?.data?.error ||
        intl.formatMessage({ id: 'roles.saveError', defaultMessage: 'Failed to save role' })
      createNotification.error(message)
    } finally {
      setSaving(false)
    }
  }

  // ── Create new role ────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!createName.trim()) {
      createNotification.error(
        intl.formatMessage({ id: 'roles.nameRequired', defaultMessage: 'Role name is required' })
      )
      return
    }
    try {
      const created = await roleServices.createRole({
        RoleName: createName.trim(),
        Description: createDescription,
        IsAdminLike: createIsAdminLike,
        IsManagerLike: createIsManagerLike,
      })
      setCreateOpen(false)
      setCreateName('')
      setCreateDescription('')
      setCreateIsAdminLike(false)
      setCreateIsManagerLike(false)
      await loadRoles(created.Id)
      createNotification.success(
        intl.formatMessage({ id: 'roles.created', defaultMessage: 'Role created' })
      )
    } catch (error: any) {
      const message = error?.response?.data?.error ||
        intl.formatMessage({ id: 'roles.createError', defaultMessage: 'Failed to create role' })
      createNotification.error(message)
    }
  }

  // ── Delete role ────────────────────────────────────────────────────────────
  const openDeleteDialog = () => {
    if (!selectedRole) return
    setReassignTo('')
    setDeleteOpen(true)
  }

  const handleDelete = async () => {
    if (!selectedRole) return
    const hasUsers = selectedRole.AssignedUserCount > 0
    if (hasUsers && !reassignTo) {
      createNotification.error(
        intl.formatMessage({
          id: 'roles.reassignRequired',
          defaultMessage: 'Select a role to reassign users to before deleting.',
        })
      )
      return
    }
    setDeleting(true)
    try {
      await roleServices.deleteRole(selectedRole.Id, hasUsers ? Number(reassignTo) : null)
      setDeleteOpen(false)
      setSelectedId(null)
      await loadRoles()
      createNotification.success(
        intl.formatMessage({ id: 'roles.deleted', defaultMessage: 'Role deleted' })
      )
    } catch (error: any) {
      const message = error?.response?.status === 409
        ? intl.formatMessage({ id: 'roles.reassignRequired', defaultMessage: 'Select a role to reassign users to before deleting.' })
        : intl.formatMessage({ id: 'roles.deleteError', defaultMessage: 'Failed to delete role' })
      createNotification.error(message)
    } finally {
      setDeleting(false)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box className={classes.root}>
      {/* Left pane */}
      <Box className={classes.leftPane}>
        <Box className={classes.leftHeader}>
          <Typography className={classes.leftTitle}>
            {intl.formatMessage({ id: 'roles.title', defaultMessage: 'Roles' })}
          </Typography>
          <MuiButton
            size="small"
            variant="contained"
            className={classes.newRoleBtn}
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            {intl.formatMessage({ id: 'roles.new', defaultMessage: 'New Role' })}
          </MuiButton>
        </Box>

        {loading ? (
          <Box className={classes.loading}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box className={classes.rolesList}>
            {roles.length === 0 ? (
              <Box className={classes.emptyState} p={2}>
                {intl.formatMessage({ id: 'roles.empty', defaultMessage: 'No roles yet. Create one to get started.' })}
              </Box>
            ) : (
              roles.map((role) => (
                <Box
                  key={role.Id}
                  className={`${classes.roleRow} ${selectedId === role.Id ? classes.roleRowActive : ''}`}
                  onClick={() => {
                    if (dirty && !window.confirm(intl.formatMessage({ id: 'roles.unsaved', defaultMessage: 'You have unsaved changes. Discard?' }))) {
                      return
                    }
                    setSelectedId(role.Id)
                  }}
                >
                  <Box className={classes.roleName}>
                    <span>{role.RoleName}</span>
                    {role.IsSystemDefault && (
                      <Chip label={intl.formatMessage({ id: 'roles.badge.default', defaultMessage: 'Default' })} size="small" className={classes.defaultBadge} />
                    )}
                    {role.IsAdminLike && (
                      <Chip label={intl.formatMessage({ id: 'roles.badge.admin', defaultMessage: 'Admin' })} size="small" color="primary" className={classes.defaultBadge} />
                    )}
                  </Box>
                  <Box className={classes.roleMeta}>
                    {intl.formatMessage(
                      role.AssignedUserCount === 1
                        ? { id: 'roles.userCount.one', defaultMessage: '{count} user' }
                        : { id: 'roles.userCount.other', defaultMessage: '{count} users' },
                      { count: role.AssignedUserCount }
                    )}
                  </Box>
                </Box>
              ))
            )}
          </Box>
        )}
      </Box>

      {/* Right pane */}
      <Box className={classes.rightPane}>
        {!selectedRole ? (
          <Box className={classes.emptyState}>
            {intl.formatMessage({ id: 'roles.select', defaultMessage: 'Select a role to view its permissions' })}
          </Box>
        ) : (
          <>
            <Box className={classes.rightScrollArea}>
              <Box className={classes.rightHeader}>
                <Box>
                  <Typography style={{ fontSize: 18, fontWeight: 600 }}>{selectedRole.RoleName}</Typography>
                  <Typography style={{ fontSize: 12, color: '#667085', marginTop: 2 }}>
                    {intl.formatMessage(
                      selectedRole.AssignedUserCount === 1
                        ? { id: 'roles.assigned.one', defaultMessage: '{count} user assigned' }
                        : { id: 'roles.assigned.other', defaultMessage: '{count} users assigned' },
                      { count: selectedRole.AssignedUserCount }
                    )}
                  </Typography>
                </Box>
                <Tooltip
                  title={
                    selectedRole.IsAdminLike && !otherAdminRolesExist
                      ? intl.formatMessage({ id: 'roles.cannotDeleteLastAdmin', defaultMessage: 'Cannot delete the last admin role' })
                      : ''
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={openDeleteDialog}
                      disabled={selectedRole.IsAdminLike && !otherAdminRolesExist}
                      style={{ color: '#EF4444' }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <Box className={classes.fieldRow}>
                <label className={classes.fieldLabel}>
                  {intl.formatMessage({ id: 'roles.name', defaultMessage: 'Name' })}
                  {' *'}
                </label>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value)
                    setDirty(true)
                  }}
                />
              </Box>

              <Box className={classes.fieldRow}>
                <label className={classes.fieldLabel}>
                  {intl.formatMessage({ id: 'roles.description', defaultMessage: 'Description' })}
                </label>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  multiline
                  minRows={2}
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value)
                    setDirty(true)
                  }}
                />
              </Box>

              <FormControlLabel
                style={{ marginBottom: 4 }}
                control={
                  <Switch
                    checked={isAdminLike}
                    onChange={(e) => {
                      setIsAdminLike(e.target.checked)
                      setDirty(true)
                    }}
                    disabled={selectedRole.IsAdminLike && !otherAdminRolesExist}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography style={{ fontSize: 13, fontWeight: 500 }}>
                      {intl.formatMessage({ id: 'roles.adminLike', defaultMessage: 'Admin-like (full access to admin-only UI)' })}
                    </Typography>
                    <Typography style={{ fontSize: 11, color: '#6B7280', lineHeight: 1.4 }}>
                      {intl.formatMessage({
                        id: 'roles.adminLike.subtitle',
                        defaultMessage: 'Users with this role will have full access to admin-only sections.',
                      })}
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                style={{ marginBottom: 4 }}
                control={
                  <Switch
                    checked={isManagerLike}
                    onChange={(e) => {
                      setIsManagerLike(e.target.checked)
                      setDirty(true)
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography style={{ fontSize: 13, fontWeight: 500 }}>
                      {intl.formatMessage({ id: 'roles.managerLike', defaultMessage: 'Managers-like (full access to manager-only UI)' })}
                    </Typography>
                    <Typography style={{ fontSize: 11, color: '#6B7280', lineHeight: 1.4 }}>
                      {intl.formatMessage({
                        id: 'roles.managerLike.subtitle',
                        defaultMessage: 'Users with this role will act as managers and have full access to manager-only sections.',
                      })}
                    </Typography>
                  </Box>
                }
              />

              <Typography style={{ fontSize: 13, fontWeight: 600, marginTop: 20, marginBottom: 4 }}>
                {intl.formatMessage({ id: 'roles.modulePermissions', defaultMessage: 'Module Permissions' })}
              </Typography>

              {(isAdminLike || isManagerLike) && (
                <Typography style={{ fontSize: 12, color: '#6B7280', marginBottom: 8, fontStyle: 'italic' }}>
                  {isAdminLike
                    ? intl.formatMessage({
                        id: 'roles.modulePermissions.adminLikeNote',
                        defaultMessage: 'Module permissions are not applicable for admin-like roles. Users with this role have full access to all modules.',
                      })
                    : intl.formatMessage({
                        id: 'roles.modulePermissions.managerLikeNote',
                        defaultMessage: 'Module permissions are not applicable for manager-like roles. Users with this role inherit the Managers role permissions.',
                      })}
                </Typography>
              )}

              <Box className={classes.matrix} style={{ opacity: (isAdminLike || isManagerLike) ? 0.4 : 1, pointerEvents: (isAdminLike || isManagerLike) ? 'none' : 'auto' }}>
                <Box className={classes.matrixHeader}>
                  <Box>{intl.formatMessage({ id: 'roles.module', defaultMessage: 'Module' })}</Box>
                  <Box>{intl.formatMessage({ id: 'roles.allowed', defaultMessage: 'Allowed' })}</Box>
                  <Box>{intl.formatMessage({ id: 'roles.visibility', defaultMessage: 'When not allowed' })}</Box>
                </Box>
                <Box className={classes.matrixBody}>
                  {MODULE_LABELS.map((m) => {
                    const row = moduleAccess.find((r) => r.ModuleId === m.id)
                    const allowed = row?.Allowed || false
                    const vis = row?.ModuleNoAccessVisibilityType ?? VISIBILITY_HIDDEN
                    return (
                      <Box key={m.id} className={classes.matrixRow}>
                        <Box>{intl.formatMessage({ id: m.labelId, defaultMessage: m.labelDefault })}</Box>
                        <Box>
                          <Switch
                            size="small"
                            checked={allowed}
                            onChange={(e) => handleAllowedChange(m.id, e.target.checked)}
                            color="primary"
                          />
                        </Box>
                        <Box>
                          <Select
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            value={vis}
                            onChange={(e) => handleVisibilityChange(m.id, Number(e.target.value))}
                            disabled={allowed}
                          >
                            <MenuItem value={VISIBILITY_HIDDEN}>
                              {intl.formatMessage({ id: 'roles.visibility.hidden', defaultMessage: 'Hidden' })}
                            </MenuItem>
                            <MenuItem value={VISIBILITY_GREYED_OUT}>
                              {intl.formatMessage({ id: 'roles.visibility.greyedOut', defaultMessage: 'Greyed-out' })}
                            </MenuItem>
                          </Select>
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              </Box>
            </Box>

            <Box className={classes.saveFooter}>
              <Button.OkButton
                click={handleSave}
                disabled={!dirty || saving}
                label={intl.formatMessage({ id: 'save', defaultMessage: 'Save' })}
              />
            </Box>
          </>
        )}
      </Box>

      {/* Create role dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{intl.formatMessage({ id: 'roles.create', defaultMessage: 'Create Role' })}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            required
            margin="dense"
            label={intl.formatMessage({ id: 'roles.name', defaultMessage: 'Name' })}
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            multiline
            minRows={2}
            label={intl.formatMessage({ id: 'roles.description', defaultMessage: 'Description' })}
            value={createDescription}
            onChange={(e) => setCreateDescription(e.target.value)}
          />
          <FormControlLabel
            control={
              <Switch
                checked={createIsAdminLike}
                onChange={(e) => setCreateIsAdminLike(e.target.checked)}
                color="primary"
              />
            }
            label={intl.formatMessage({ id: 'roles.adminLike', defaultMessage: 'Admin-like' })}
          />
          <FormControlLabel
            control={
              <Switch
                checked={createIsManagerLike}
                onChange={(e) => setCreateIsManagerLike(e.target.checked)}
                color="primary"
              />
            }
            label={intl.formatMessage({ id: 'roles.managerLike', defaultMessage: 'Managers-like' })}
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setCreateOpen(false)}>
            {intl.formatMessage({ id: 'cancel', defaultMessage: 'Cancel' })}
          </MuiButton>
          <MuiButton onClick={handleCreate} color="primary" variant="contained">
            {intl.formatMessage({ id: 'create', defaultMessage: 'Create' })}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{intl.formatMessage({ id: 'roles.delete.title', defaultMessage: 'Delete Role' })}</DialogTitle>
        <DialogContent>
          {selectedRole && selectedRole.AssignedUserCount > 0 ? (
            <>
              <Typography style={{ marginBottom: 12 }}>
                {intl.formatMessage(
                  selectedRole.AssignedUserCount === 1
                    ? { id: 'roles.delete.reassign.one',   defaultMessage: '{count} user is assigned to this role. Reassign them to:' }
                    : { id: 'roles.delete.reassign.other', defaultMessage: '{count} users are assigned to this role. Reassign them to:' },
                  { count: selectedRole.AssignedUserCount }
                )}
              </Typography>
              <Select
                fullWidth
                variant="outlined"
                value={reassignTo}
                onChange={(e) => setReassignTo(Number(e.target.value))}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  {intl.formatMessage({ id: 'roles.selectRole', defaultMessage: 'Select a role…' })}
                </MenuItem>
                {roles
                  .filter((r) => r.Id !== selectedRole.Id)
                  .map((r) => (
                    <MenuItem key={r.Id} value={r.Id}>
                      {r.RoleName}
                    </MenuItem>
                  ))}
              </Select>
            </>
          ) : (
            <Typography>
              {intl.formatMessage({ id: 'roles.delete.confirm', defaultMessage: 'Are you sure you want to delete this role?' })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setDeleteOpen(false)} disabled={deleting}>
            {intl.formatMessage({ id: 'cancel', defaultMessage: 'Cancel' })}
          </MuiButton>
          <MuiButton onClick={handleDelete} color="secondary" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={16} /> : intl.formatMessage({ id: 'delete', defaultMessage: 'Delete' })}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default SecurityPage
